import type {SortDirection, TableRow, TableValue} from './model';

function compareValues(a: TableValue, b: TableValue, collator: Intl.Collator): number {
    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }

    return collator.compare(String(a), String(b));
}

/**
 * Rows ordered by one column. Totals stay pinned first and empty values last in either direction; gap rows drop out,
 * they only mean something in the order the table was built in. Group rows stay in place and the rows sort within
 * each group. The sort is stable, so ties keep the built order.
 */
export function sortRows(rows: TableRow[], column: string, dir: SortDirection, locale: string): TableRow[] {
    const collator = new Intl.Collator(locale, {numeric: true});
    const sign = dir === 'asc' ? 1 : -1;
    const valueOf = (row: TableRow): TableValue => row.cells[column]?.value ?? null;
    const sort = (block: TableRow[]): TableRow[] => [...block].sort((a, b) => {
        const left = valueOf(a);
        const right = valueOf(b);
        if (left === null || right === null) {
            return (left === null ? 1 : 0) - (right === null ? 1 : 0);
        }

        return sign * compareValues(left, right, collator);
    });

    const sorted: TableRow[] = rows.filter(row => row.role === 'total');
    let block: TableRow[] = [];
    for (const row of rows) {
        if (row.role === 'group') {
            sorted.push(...sort(block), row);
            block = [];
        } else if (row.role === 'data') {
            block.push(row);
        }
    }

    return [...sorted, ...sort(block)];
}
